import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Table, TableHead, TableBody,
    TableRow, TableCell, TextField, Chip, IconButton, CircularProgress, Paper
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { TaxService } from '../../services/tax.service';
import { ITaxYear, ITaxSlab } from '../../interfaces/tax.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';

const TaxSlabList: React.FC = () => {
    const { showSuccess, showError, showApiError } = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [taxYears, setTaxYears] = useState<ITaxYear[]>([]);
    const [selectedYearId, setSelectedYearId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showNewYearForm, setShowNewYearForm] = useState(false);
    const [newYear, setNewYear] = useState({ label: '', startDate: '', endDate: '' });

    const load = () => {
        TaxService.getTaxYears(institutionId)
            .then(years => {
                setTaxYears(years);
                if (!selectedYearId && years.length > 0) {
                    const active = years.find(y => y.isActive) ?? years[0];
                    setSelectedYearId(active.id!);
                }
            })
            .catch(() => showError('Failed to load tax years'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [institutionId]);

    const selectedYear = taxYears.find(y => y.id === selectedYearId);

    const updateSlab = (idx: number, field: keyof ITaxSlab, value: string) => {
        if (!selectedYear) return;
        const slabs = [...selectedYear.slabs];
        slabs[idx] = { ...slabs[idx], [field]: value === '' ? undefined : parseFloat(value) };
        setTaxYears(years => years.map(y => y.id === selectedYearId ? { ...y, slabs } : y));
    };

    const addRow = () => {
        if (!selectedYear) return;
        const slabs = [...selectedYear.slabs, { incomeFrom: 0, rate: 0 }];
        setTaxYears(years => years.map(y => y.id === selectedYearId ? { ...y, slabs } : y));
    };

    const removeRow = (idx: number) => {
        if (!selectedYear) return;
        const slabs = selectedYear.slabs.filter((_, i) => i !== idx);
        setTaxYears(years => years.map(y => y.id === selectedYearId ? { ...y, slabs } : y));
    };

    const saveSlabs = async () => {
        if (!selectedYear) return;
        setSaving(true);
        try {
            await TaxService.saveTaxYear({ ...selectedYear, institutionId });
            showSuccess('Tax slabs saved');
            load();
        } catch (error) {
            showApiError(error, 'Failed to save slabs');
        } finally { setSaving(false); }
    };

    const activate = async (id: string) => {
        try {
            await TaxService.activateTaxYear(id, institutionId);
            load();
        } catch (error) {
            showApiError(error, 'Failed to activate tax year');
        }
    };

    const createYear = async () => {
        if (!newYear.label.trim() || !newYear.startDate || !newYear.endDate) {
            showError('Enter a label, start date and end date for the tax year');
            return;
        }
        try {
            // Starter slab: everything taxed at 0% until the real slabs are entered in the table.
            await TaxService.saveTaxYear({ ...newYear, institutionId, slabs: [{ incomeFrom: 0, rate: 0 }] });
            showSuccess('Tax year created - now edit its slabs below');
            setShowNewYearForm(false);
            setNewYear({ label: '', startDate: '', endDate: '' });
            load();
        } catch (error) {
            showApiError(error, 'Failed to create tax year');
        }
    };

    if (loading) return <CircularProgress />;

    return (
        <Box data-help-page="tax.slabs.page" p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Tax Slabs</Typography>
                <Button data-help="tax.slabs.newYear" variant="outlined" onClick={() => setShowNewYearForm(!showNewYearForm)}>
                    + New Tax Year
                </Button>
            </Box>

            {showNewYearForm && (
                <Paper data-help-section="tax.slabs.newYearForm" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField data-help="tax.slabs.field.label" size="small" label="Label (e.g. FY 2025-26)" value={newYear.label}
                        onChange={e => setNewYear(n => ({ ...n, label: e.target.value }))} />
                    <TextField data-help="tax.slabs.field.startDate" size="small" label="Start Date" type="date" InputLabelProps={{ shrink: true }}
                        value={newYear.startDate} onChange={e => setNewYear(n => ({ ...n, startDate: e.target.value }))} />
                    <TextField data-help="tax.slabs.field.endDate" size="small" label="End Date" type="date" InputLabelProps={{ shrink: true }}
                        value={newYear.endDate} onChange={e => setNewYear(n => ({ ...n, endDate: e.target.value }))} />
                    <Button data-help="tax.slabs.createYear" variant="contained" size="small" onClick={createYear}>Create</Button>
                </Paper>
            )}

            <Box data-help-section="tax.slabs.years" display="flex" gap={1} mb={3} flexWrap="wrap">
                {taxYears.map(y => (
                    <Chip data-help="tax.slabs.yearChip" key={y.id}
                        label={`${y.label}${y.isActive ? ' ✓ Active' : ''}`}
                        color={y.id === selectedYearId ? 'primary' : 'default'}
                        variant={y.id === selectedYearId ? 'filled' : 'outlined'}
                        onClick={() => setSelectedYearId(y.id!)}
                        onDelete={!y.isActive ? () => activate(y.id!) : undefined}
                        deleteIcon={!y.isActive ? <span style={{ fontSize: 10, padding: '0 4px' }}>Set Active</span> : undefined}
                    />
                ))}
            </Box>

            {selectedYear && (
                <Paper>
                    <ScrollableTable>
                        <Table data-help-section="tax.slabs.table" size="small">
                            <TableHead>
                                <TableRow sx={{ background: '#f1f5f9' }}>
                                    <TableCell data-help="tax.slabs.col.order">#</TableCell>
                                    <TableCell data-help="tax.slabs.col.from">Annual Income From</TableCell>
                                    <TableCell data-help="tax.slabs.col.to">To (blank = ∞)</TableCell>
                                    <TableCell data-help="tax.slabs.col.rate">Rate %</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedYear.slabs.map((slab, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>{idx + 1}</TableCell>
                                        <TableCell>
                                            <TextField data-help="tax.slabs.line.from" size="small" type="number" sx={{ minWidth: 120 }}
                                                value={slab.incomeFrom ?? ''}
                                                onChange={e => updateSlab(idx, 'incomeFrom', e.target.value)} />
                                        </TableCell>
                                        <TableCell>
                                            <TextField data-help="tax.slabs.line.to" size="small" type="number" sx={{ minWidth: 120 }}
                                                placeholder="∞"
                                                value={slab.incomeTo ?? ''}
                                                onChange={e => updateSlab(idx, 'incomeTo', e.target.value)} />
                                        </TableCell>
                                        <TableCell>
                                            <TextField data-help="tax.slabs.line.rate" size="small" type="number" sx={{ minWidth: 90 }}
                                                value={slab.rate ?? ''}
                                                onChange={e => updateSlab(idx, 'rate', e.target.value)} />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton data-help="tax.slabs.action.remove" size="small" onClick={() => removeRow(idx)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </ScrollableTable>
                    <Box p={2} display="flex" gap={2} alignItems="center">
                        <Button data-help="tax.slabs.action.addRow" size="small" onClick={addRow}>+ Add Row</Button>
                        <Button data-help="tax.slabs.action.save" variant="contained" size="small" onClick={saveSlabs} disabled={saving}>
                            {saving ? 'Saving...' : 'Save Slabs'}
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default TaxSlabList;
